import { useMemo, useState } from "react";
import { SmilePlus } from "lucide-react";
import { MESSAGE_REACTION_EMOJIS } from "@shared/schema";

export type ReactionMap = Record<string, string> | null | undefined;

// Mirror of the server toggle so optimistic updates match what the API returns.
export function toggleReaction(
  reactions: ReactionMap,
  userId: string,
  emoji: string,
): Record<string, string> {
  const next = { ...(reactions ?? {}) };
  if (next[userId] === emoji) delete next[userId];
  else next[userId] = emoji;
  return next;
}

type Props = {
  reactions: ReactionMap;
  currentUserId?: string;
  onReact: (emoji: string) => void;
  align: "start" | "end";
};

export function MessageReactions({ reactions, currentUserId, onReact, align }: Props) {
  const [open, setOpen] = useState(false);

  const { counts, mine } = useMemo(() => {
    const counts: Record<string, number> = {};
    const mine = new Set<string>();
    for (const [uid, emoji] of Object.entries(reactions ?? {})) {
      if (!emoji) continue;
      counts[emoji] = (counts[emoji] ?? 0) + 1;
      if (uid === currentUserId) mine.add(emoji);
    }
    return { counts, mine };
  }, [reactions, currentUserId]);

  const hasReactions = Object.keys(counts).length > 0;

  const react = (emoji: string) => {
    setOpen(false);
    onReact(emoji);
  };

  return (
    <div className={`flex flex-col gap-1 ${align === "end" ? "items-end" : "items-start"}`}>
      <div className={`flex items-center gap-1 ${align === "end" ? "flex-row-reverse" : ""}`}>
        <button
          type="button"
          aria-label="Add reaction"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full p-0.5 text-muted-foreground/40 transition hover:bg-muted hover:text-muted-foreground"
        >
          <SmilePlus className="h-3.5 w-3.5" />
        </button>
        {hasReactions &&
          Object.entries(counts).map(([emoji, n]) => (
            <button
              key={emoji}
              type="button"
              onClick={() => onReact(emoji)}
              className={`flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px] leading-none transition ${
                mine.has(emoji)
                  ? "border-primary/50 bg-primary/10"
                  : "border-transparent bg-muted"
              }`}
            >
              <span>{emoji}</span>
              {n > 1 && <span className="text-[10px] text-muted-foreground">{n}</span>}
            </button>
          ))}
      </div>

      {open && (
        <div className="flex gap-1.5 rounded-full border bg-background px-2.5 py-1.5 shadow-sm">
          {MESSAGE_REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => react(emoji)}
              className="text-lg leading-none transition hover:scale-125"
              aria-label={`React ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
